import axios from 'axios'

const OFFER_BOARD = 'offerBoard'

// How long an Offer Board listing stays on the Livestock Exchange, by stock
// type. The API applies the same defaults, this is only what the form suggests.
const expiryDaysByStockType = {
  cattle: 28,
  sheep: 10,
  lamb: 10,
  goat: 10
}

const defaultExpiryDays = 10

// Each category expires on its own schedule, so the admin fills in one expiry
// per chosen category and this is the suggested default for it.
const getExpiryDays = (kind) => {
  const stockType = `${(kind && (kind.type || kind.title)) || ''}`.toLowerCase()
  const match = Object.keys(expiryDaysByStockType)
    .find(key => stockType.includes(key))
  return match ? expiryDaysByStockType[match] : defaultExpiryDays
}

// Keeps the days already typed in for the categories that stay chosen and
// drops the ones that were removed.
const getExpiryDaysMap = (kinds = [], current = {}) => {
  return kinds.reduce((expiryDays, kind) => {
    const chosen = current && current[kind.id]
    return {
      ...expiryDays,
      [kind.id]: chosen === undefined || chosen === null || chosen === '' ? getExpiryDays(kind) : chosen
    }
  }, {})
}

// The API stores one category per stock type, the form works with a list of
// kind ids and a { kindId: days } map, so both shapes are converted here.
const toCategories = (values = {}) => {
  const listingExpiryDays = values.listingExpiryDays || {}
  return (values.kindIds || []).map(kindId => ({
    kind: kindId,
    listingExpiryDays: Number(listingExpiryDays[kindId]) || null
  }))
}

const fromOfferBoard = (offerBoard) => {
  if (!offerBoard) {
    return null
  }
  const categories = offerBoard.categories || []
  return {
    ...offerBoard,
    id: offerBoard._id || offerBoard.id,
    kindIds: categories.map(category => category.kind),
    listingExpiryDays: categories.reduce((expiryDays, category) => ({
      ...expiryDays,
      [category.kind]: category.listingExpiryDays
    }), {})
  }
}

const toPayload = (values = {}) => ({
  offerBoard: {
    title: values.title,
    approved: Boolean(values.approved),
    public: Boolean(values.public),
    adminNotes: values.adminNotes || null,
    location: { state: (values.location && values.location.state) || [] },
    categories: toCategories(values)
  }
})

// What went wrong reads off the API response, which carries a message per
// field, and falls back to the error itself when it carries none.
const errorMessage = (err, fallback = 'Offer Board could not be saved') => {
  const data = err && err.response && err.response.data
  const errors = data && data.error && data.error.errors
  if (errors) {
    const messages = Object.keys(errors)
      .map(key => errors[key] && (errors[key].message || errors[key]))
      .filter(one => typeof one === 'string')
    if (messages.length) {
      return messages.join(', ')
    }
  }
  return (err && err.message) || fallback
}

// Every board that has not been archived, newest first. Several boards run at
// the same time, each one is set up and worked with on its own.
const getOfferBoards = async () => {
  const res = await axios.get('/offer-boards', {
    params: { archived: false, sortField: 'createdAt', sortDirection: -1 }
  })
  const offerBoards = (res.data && res.data.offerBoards) || []
  return offerBoards.map(fromOfferBoard)
}

// Every board that has been archived, newest first. They are only read back -
// the board, its listings and its offers are all still there.
const getArchivedOfferBoards = async () => {
  const res = await axios.get('/offer-boards', {
    params: { archived: true, sortField: 'archivedAt', sortDirection: -1 }
  })
  const offerBoards = (res.data && res.data.offerBoards) || []
  return offerBoards.map(fromOfferBoard)
}

const createOfferBoard = async (values) => {
  const res = await axios.post('/offer-boards', toPayload(values))
  return fromOfferBoard(res.data && res.data.offerBoard)
}

const updateOfferBoard = async (offerBoardId, values) => {
  const res = await axios.put(`/offer-boards/${offerBoardId}`, toPayload(values))
  return fromOfferBoard(res.data && res.data.offerBoard)
}

const saveOfferBoard = (values, current = null) => {
  const offerBoardId = current && (current.id || current._id)
  return offerBoardId ? updateOfferBoard(offerBoardId, values) : createOfferBoard(values)
}

const getOfferBoardLots = async (offerBoardId, params = {}) => {
  const res = await axios.get(`/offer-boards/${offerBoardId}/lots`, {
    params: { fromAdmin: true, ...params }
  })
  return (res.data && res.data.lots) || []
}

// every offer made on the board, an admin sees them all
const getOfferBoardOffers = async (offerBoardId, params = {}) => {
  const res = await axios.get(`/offer-boards/${offerBoardId}/offers`, { params })
  return (res.data && res.data.offers) || []
}

// an admin answers an offer on the Seller's behalf: accept, decline or counter
const answerOffer = async (offerId, action, cents) => {
  const res = await axios.put(
    `/offers/${offerId}/${action}`,
    cents ? { offer: { cents } } : undefined
  )
  return (res.data && res.data.offer) || null
}

// archiving a board finishes with it: it comes off the webapp, while everything
// that was on it is kept to be read back from the Archived section
const archiveOfferBoard = async (offerBoardId) => {
  const res = await axios.put(`/offer-boards/${offerBoardId}/archive`)
  return fromOfferBoard(res.data && res.data.offerBoard)
}

// deleting a board takes its offers and its listings off it
const deleteOfferBoard = async (offerBoardId) => {
  await axios.delete(`/offer-boards/${offerBoardId}`)
  return true
}

export default {
  OFFER_BOARD,
  errorMessage,
  expiryDaysByStockType,
  defaultExpiryDays,
  getExpiryDays,
  getExpiryDaysMap,
  getOfferBoards,
  getArchivedOfferBoards,
  getOfferBoardLots,
  getOfferBoardOffers,
  answerOffer,
  saveOfferBoard,
  archiveOfferBoard,
  deleteOfferBoard
}
